import React from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Card, Select, LogoLoader } from "../../../components/ui";

const MonthlyTrendChart = ({
  trendMonth,
  setTrendMonth,
  trendYear,
  setTrendYear,
  trendData,
  isTrendLoading,
  CustomTooltip
}) => {
  return (
    <Card className="lg:col-span-1">
      <Card.Header
        title="Monthly Deposits & Withdrawals Trend"
        actions={
          <div className="flex items-center gap-2">
            <Select
              value={trendMonth}
              onChange={(e) => setTrendMonth(e.target.value)}
              options={[
                { value: "all", label: "Last 90 Days" },
                { value: "01", label: "January" },
                { value: "02", label: "February" },
                { value: "03", label: "March" },
                { value: "04", label: "April" },
                { value: "05", label: "May" },
                { value: "06", label: "June" },
                { value: "07", label: "July" },
                { value: "08", label: "August" },
                { value: "09", label: "September" },
                { value: "10", label: "October" },
                { value: "11", label: "November" },
                { value: "12", label: "December" },
              ]}
            />
            {trendMonth !== "all" && (
              <Select
                value={trendYear}
                onChange={(e) => setTrendYear(e.target.value)}
                options={[
                  { value: "2026", label: "2026" },
                  { value: "2025", label: "2025" },
                  { value: "2024", label: "2024" },
                ]}
              />
            )}
          </div>
        }
      />
      <Card.Body className="relative min-h-[280px]">
        <div className="flex justify-center gap-6 mb-6 text-xs font-semibold text-ink-700">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-primary-500 inline-block" />
            <span>Deposits</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-danger-500 inline-block" />
            <span>Withdrawals</span>
          </div>
        </div>

        <div className="relative min-h-[250px]">
          {isTrendLoading && (
            <div className="absolute inset-0 bg-white/70 backdrop-blur-xs flex items-center justify-center z-10">
              <LogoLoader size="sm" />
            </div>
          )}
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart
              data={trendData || []}
              margin={{ top: 10, right: 40, left: 10, bottom: 10 }}
            >
              <defs>
                <linearGradient id="colorDeposits" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-primary-500)" stopOpacity={0.15}/>
                  <stop offset="95%" stopColor="var(--color-primary-500)" stopOpacity={0.01}/>
                </linearGradient>
                <linearGradient id="colorWithdrawals" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-danger)" stopOpacity={0.15}/>
                  <stop offset="95%" stopColor="var(--color-danger)" stopOpacity={0.01}/>
                </linearGradient>
              </defs>
              <CartesianGrid
                stroke="#E2E8F0"
                strokeDasharray="3 3"
                vertical={false}
              />
              <XAxis
                dataKey="month"
                stroke="#94A3B8"
                tick={{ fill: "#64748B", fontSize: 11, fontWeight: 500 }}
                axisLine={false}
                tickLine={false}
                dy={10}
              />
              <YAxis
                stroke="#94A3B8"
                tick={{ fill: "#64748B", fontSize: 11, fontWeight: 500 }}
                tickFormatter={(value) => {
                  const num = Number(value);
                  if (isNaN(num)) return "";
                  if (num === 0) return "₹0";
                  if (num >= 100000) {
                    return `₹${(num / 100000).toFixed(0)}L`;
                  }
                  return `₹${num.toLocaleString("en-IN")}`;
                }}
                axisLine={false}
                tickLine={false}
                dx={-10}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="deposits"
                stroke="var(--color-primary-500)"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorDeposits)"
                name="Deposits"
                dot={{
                  r: 4,
                  fill: "var(--color-primary-500)",
                  strokeWidth: 1.5,
                  stroke: "white",
                }}
                activeDot={{ r: 6, stroke: "white", strokeWidth: 2 }}
              />
              <Area
                type="monotone"
                dataKey="withdrawals"
                stroke="var(--color-danger)"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorWithdrawals)"
                name="Withdrawals"
                dot={{
                  r: 4,
                  fill: "var(--color-danger)",
                  strokeWidth: 1.5,
                  stroke: "white",
                }}
                activeDot={{ r: 6, stroke: "white", strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card.Body>
    </Card>
  );
};

export default MonthlyTrendChart;
